import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Scrolling row of action chips. Each chip is written the way a client would say it, and clicking
// sends exactly that text to Aura — what you tap is what appears in the chat.
// Each maps to a tool in the backend (customer_support_bot_backend/main_api.py).
export const TICKER_ITEMS = [
  // Order here doesn't matter: the row is shuffled on every load (see shuffled below)
  'Where is my case?',                       // fetch_case_details
  'When will my report be ready?',           // get_eta
  'Send my report on WhatsApp',              // send_report_to_user
  'Prioritise my urgent case',               // mark_as_critical
  'Raise a rework on my report',             // raise_rework
  'Why does my case need attention?',        // get_attention_required_reason
  'Find a case by patient name',             // fetch_case_details_by_patient_details
  "Show today's cases",                      // fetch_all_case_details
  "Show yesterday's cases",                  // fetch_cases_by_date_reference
  "Show this week's cases",                  // fetch_cases_by_date_reference
  'Summarise my report findings',            // get_report_content
  'Download my report',                      // get_report_urls
  'Email my report',                         // send_report_to_user
  'Get provisional report',                  // get_provisional_report_by_order_id
  'Update my case history',                  // modify_case_history
  'Show my completed cases',                 // fetch_all_case_details
  'Show my draft cases',                     // get_all_draft_cases
  "Show this week's draft cases",            // fetch_draft_cases_by_date_reference
  'Connect me to the radiologist',           // refering_physician_connect
  'Request a callback',                      // contact_customer_support
  'Talk to customer support',                // contact_customer_support
  'I have a technical issue',                // contact_technical_support
  'I have a billing question',               // contact_finance_support
  'Download my invoice',                     // get_invoice_download_link (logins without Payments access are told to contact finance)
  "My images aren't showing in Drafts",      // get_technical_help
  'How do I upload images?',                 // get_technical_help
  'Show my account details',                 // get_client_info
  'Give feedback',                           // escalate_feedback_to_management
  'Refer 5C to a colleague',                 // referral info
  // Actions that change or remove a case; Aura asks for the order ID before acting
  'Mark a case as non-reportable',           // mark_case_as_non_reportable
  'Move an active case back to draft',       // delete_active_case
  'Delete a draft case',                     // delete_draft_case
  // skip_qc is intentionally not listed (removed on request)
];

// Keep the scroll speed constant however many chips there are (~4.5s of travel per chip)
const LOOP_SECONDS = Math.round(TICKER_ITEMS.length * 4.5);

const scroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const Strip = styled.div`
  position: relative;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: #f1f3f6;
  border-bottom: 1px solid #e2e8f0;
  /* soft fade at both edges so chips don't hard-cut */
  mask-image: linear-gradient(to right, transparent 0, #000 20px, #000 calc(100% - 20px), transparent 100%);
  -webkit-mask-image: linear-gradient(to right, transparent 0, #000 20px, #000 calc(100% - 20px), transparent 100%);
`;

const Track = styled.div`
  display: flex;
  gap: 8px;
  padding-right: 8px; /* keeps the gap even across the loop seam */
  width: max-content;
  animation: ${scroll} ${LOOP_SECONDS}s linear infinite;

  /* stop moving while the user is aiming at a chip or tabbing through them */
  ${Strip}:hover &,
  ${Strip}:focus-within & {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Chip = styled.button`
  flex-shrink: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 5px 12px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #275386;
    color: #275386;
    background: #e7edf3;
  }

  &:focus-visible {
    outline: 2px solid #275386;
    outline-offset: 1px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

// Fisher-Yates shuffle
const shuffled = (items) => {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const SuggestionTicker = ({ onSelect, disabled = false }) => {
  // Shuffle once per load (not per render) so the row doesn't reorder mid-scroll,
  // and returning users see different chips first
  const [items] = useState(() => shuffled(TICKER_ITEMS));
  const renderChips = (copy) =>
    items.map((text) => (
      <Chip
        key={`${copy}-${text}`}
        onClick={() => onSelect(text)}
        disabled={disabled}
        // second copy only exists to make the loop seamless: hide it from screen readers and tab order
        aria-hidden={copy === 'dup' ? 'true' : undefined}
        tabIndex={copy === 'dup' ? -1 : undefined}
      >
        {text}
      </Chip>
    ));

  return (
    <Strip role="group" aria-label="Things you can ask Aura">
      <Track>
        {renderChips('main')}
        {renderChips('dup')}
      </Track>
    </Strip>
  );
};

export default SuggestionTicker;
